import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import TopBar from 'TailwindcssComponents/TopBar';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import { Icon } from "@iconify/react";

export default function EditVenueCity() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            editCity.setFieldValue("image", file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const fetchVenueCity = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/city/${id}`);
            if (response.data.success && response.data.data) {
                const city = response.data?.data;
                editCity.setValues({
                    name: city.name,
                    location: city.location,
                    description: city.description,
                    lat: city.coordinate?.lat || '',
                    lng: city.coordinate?.lng || '',
                    image: city.image?.secure_url || null,
                });
                setPreviewImage(city.image?.secure_url || null);
            }
        } catch (error) {
            console.error('Error fetching venue data:', error.message);
        }
    };

    useEffect(() => {
        fetchVenueCity();
    }, [id]);

    const editCity = useFormik({
        initialValues: {
            name: '',
            location: '',
            description: '',
            image: null,
            lat: '',
            lng: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
            description: Yup.string().required('Required'),
            location: Yup.string()
                .matches(
                    /^https:\/\/www\.google\.com\/maps\/embed\?pb=.*/i,
                    'Please enter a valid embedded Google Maps URL'
                )
                .required('Location URL is required'),
            lat: Yup.number()
                .typeError('Latitude must be a decimal number')
                .required('Latitude is required')
                .min(-90, 'Latitude must be at least -90')
                .max(90, 'Latitude must be at most 90'),
            lng: Yup.number()
                .typeError('Longitude must be a decimal number')
                .required('Longitude is required')
                .min(-180, 'Longitude must be at least -180')
                .max(180, 'Longitude must be at most 180'),
            image: Yup.mixed()
                .required('Image is required')
                .test('fileSize', "File size must be less than 1MB", (value) => {
                    return value && value.size <= 1 * 1024 * 1024; // 1MB
                })
                .test('fileFormat', 'Unsupported Format', (value) => {
                    return value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type);
                }),
        }),
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                const formData = new FormData();
                formData.append('name', values.name);
                formData.append('location', values.location);
                formData.append('description', values.description);
                formData.append('image', values.image || null);
                formData.append("coordinate", JSON.stringify({
                    lat: parseFloat(values.lat),
                    lng: parseFloat(values.lng)
                }));
                const response = await axios.put(`${apiUrl}event-service/city/${id}`, formData, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'multipart/form-data',
                        'accept': 'application/json',
                    },
                });

                if (response.data?.success) {
                    resetForm();
                    setTimeout(() => {
                        navigate('/venue-cities');
                    }, 500);
                    toast.success(response.data?.message || 'Venue city has been successfully updated!');
                } else {
                    throw new Error('Failed to update venue city');
                }
            } catch (error) {
                console.error('Error submitting form:', error.response?.data?.message || error.message);
                toast.error(error.response?.data?.message || error.message);
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <TailwindLayout>
            <TopBar />
            <div className="max-w-7xl mx-auto p-12 sm:p-12 lg:p-12 mb-8">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">

                    <form onSubmit={editCity.handleSubmit} className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Edit Venue City</h2>
                        <div className='border-t pt-6'>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2" htmlFor="name">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Name"
                                    value={editCity.values.name}
                                    onChange={editCity.handleChange}
                                    onBlur={editCity.handleBlur}
                                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                />
                                {editCity.touched.name && editCity.errors.name && <div className="text-red-500 text-sm">{editCity.errors.name}</div>}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2" htmlFor="location">
                                    Location <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    placeholder="eg. https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.1234567890123!2d-122.4194156846814!3d37.7749292797598!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808c8c8c8c8c%3A0x8c8c8c8c8c8c8c8c%2C+San+Francisco%2C+CA+94103%2C+USA!5e0!3m2!1sen!2sus!4v1234567890123"
                                    value={editCity.values.location}
                                    onChange={editCity.handleChange}
                                    onBlur={editCity.handleBlur}
                                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                />
                                {editCity.touched.location && editCity.errors.location && <div className="text-red-500 text-sm">{editCity.errors.location}</div>}
                            </div>
                            <div className='mb-4'>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Latitude <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="lat"
                                            placeholder="eg. 37.7749"
                                            value={editCity.values.lat}
                                            onChange={editCity.handleChange}
                                            onBlur={editCity.handleBlur}
                                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                        />
                                        {editCity.touched.lat && editCity.errors.lat && <div className="text-red-500 text-sm">{editCity.errors.lat}</div>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Longitude <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="lng"
                                            placeholder="eg. 122.4194"
                                            value={editCity.values.lng}
                                            onChange={editCity.handleChange}
                                            onBlur={editCity.handleBlur}
                                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                        />
                                        {editCity.touched.lng && editCity.errors.lng && <div className="text-red-500 text-sm">{editCity.errors.lng}</div>}
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2" htmlFor="description">
                                    description <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="description"
                                    value={editCity.values.description}
                                    onChange={editCity.handleChange}
                                    onBlur={editCity.handleBlur}
                                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                />
                                {editCity.touched.description && editCity.errors.description && <div className="text-red-500 text-sm">{editCity.errors.description}</div>}
                            </div>
                            {/* <div className='mb-4'>
                                <label className="block text-sm font-medium mb-2" htmlFor="address">
                                    Image
                                </label>
                                <input
                                    type="file"
                                    name="image"
                                    onChange={handleImageChange}
                                    // onChange={(event) => editCity.setFieldValue("image", event.currentTarget.files[0])}
                                    onBlur={() => editCity.setFieldTouched("image", true)}
                                    // className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    accept="image/*"
                                    className='hidden'
                                />
                                {previewImage && (
                                    <div className="mb-2 mt-2">
                                        <img
                                            src={previewImage}
                                            alt="Preview"
                                            className="w-32 h-32 object-cover rounded-md border"
                                        />
                                    </div>
                                )}
                                {editCity.touched.image && editCity.errors.image && (
                                    <p className="text-red-500 text-sm mt-1">{editCity.errors.image}</p>
                                )}
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('hiddenFileInput').click()}
                                    className="px-4 py-2 bg-gray-200 text-sm rounded hover:bg-gray-300 transition"
                                >
                                    {previewImage ? 'Change Image' : 'Upload Image'}
                                </button>
                                <label htmlFor="image-upload" className="cursor-pointer">
                                    <span className="block text-center text-xs text-gray-400 mt-2">Recommended size: below 5mb & 1024x1024px or 1:1. Valid file formats: JPG, PNG.</span>
                                </label>
                            </div> */}
                            <div className='mb-4'>
                                <div className="text-sm font-medium mb-2">Image <span className="text-gray-500">(Recommended size: below 1mb, 1:1, JPG/JPEG/PNG format)</span></div>
                                <div className="flex items-center space-x-4">
                                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center">
                                        {previewImage ? (
                                            <img src={previewImage} alt="City image" className="w-full h-full object-cover rounded-md" />
                                        ) : (
                                            <Icon icon="iconoir:add-media-image" className="text-xl" />
                                        )}
                                    </div>
                                    <label className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 cursor-pointer">
                                        <span>{previewImage ? 'Change Image' : 'Upload Image'}</span>
                                        <input type="file" className="hidden" onChange={handleImageChange} />
                                    </label>
                                </div>
                                {editCity.touched.image && editCity.errors.image && (
                                    <p className="text-red-500 text-sm mt-1">{editCity.errors.image}</p>
                                )}
                            </div>
                        </div>
                        <div className="mt-6 flex items-center">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <FooterTailwind />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </TailwindLayout>
    )
}